import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { NAVY, GOLD, TEXT_SECONDARY, ERROR } from "../../theme/tokens";

export interface FormCardProps {
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactElement;
  /** Shows a small "Required" pill next to the title. */
  required?: boolean;
  /** Right-aligned header slot (e.g. a small toggle or add link). */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Titled card section for forms — mirrors the premium "Basic Information /
 * Required" card pattern. Compose several FormCards down a create/edit page.
 */
export default function FormCard({ title, subtitle, icon, required, headerAction, children }: FormCardProps) {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(NAVY, 0.06),
              border: `1px solid ${alpha(GOLD, 0.3)}`,
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 20, color: NAVY } })}
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography fontSize={15} fontWeight={800} color={NAVY}>
              {title}
            </Typography>
            {required && (
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  color: ERROR,
                  bgcolor: alpha(ERROR, 0.1),
                }}
              >
                Required
              </Box>
            )}
          </Box>
          {subtitle && (
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerAction}
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
  );
}
